//
//  QaProgramChecklistNoteComposer.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module renders the add-only QA checklist note composer.
//

import { Button, Group, Stack } from "@mantine/core";
import {
    FieldPropertyPathProvider,
    Form,
    useFormManager,
    ValidationErrorType,
    ValidationTrigger,
    ValidatorType,
    type FieldValidationContext,
    type FormValidators
} from "@ngt-form/core";
import {
    FormActionButton,
    GroupedTableField,
    TextAreaInput,
    type GroupedTableFieldColumn
} from "@ngt-form/mantine";
import { useEffect, useMemo, type FC } from "react";

import {
    FileState,
    type LookupOption,
    SourceDocument,
    type TemporaryFileInformation,
    TrialQaProgramChecklistItemNote
} from "src/api/Tmd.dtos";
import LongGroupedField from "src/components/forms/fields/grouped/LongGroupedField";
import {
    FileUploadInput,
    type FileUploadInputViewProps
} from "src/components/forms/inputs/FileUploadInput";
import FileUploadInputDefaultView from "src/components/forms/inputs/FiledUploadInput.DefaultView";
import useTMDFormAllowSubmit from "src/hooks/form/useTMDFormAllowSubmit";
import useTMDFormOnSubmitFailed from "src/hooks/form/useTMDFormOnSubmitFailed";
import useTMDFormOnSubmitNotAllowed from "src/hooks/form/useTMDFormOnSubmitNotAllowed";
import type { TMDValidationError } from "src/types/TMDValidationError";

const validators: FormValidators<TrialQaProgramChecklistItemNote, TMDValidationError> = [
    {
        type: ValidatorType.Field,
        propertyPath: "comments",
        trigger: ValidationTrigger.Change,
        subscription: { value: true },
        validate: async ({ fieldSnapshot }) =>
            typeof fieldSnapshot.value === "string" && fieldSnapshot.value.trim()
                ? {}
                : {
                      fields: {
                          comments: [
                              {
                                  code: "QA-NOTE-001",
                                  type: ValidationErrorType.Medium,
                                  message: "'Message' is required to be entered.",
                                  meta: { frontEndError: true, blocking: true }
                              }
                          ]
                      }
                  }
    },
    {
        type: ValidatorType.DeindexedField,
        deindexedPropertyPath: "attachments.fileInformation",
        trigger: ValidationTrigger.Change,
        subscription: { value: true },
        validate: (
            propertyPath,
            context: FieldValidationContext<
                TrialQaProgramChecklistItemNote,
                TMDValidationError,
                object,
                TemporaryFileInformation
            >
        ) => {
            const state = context.fieldSnapshot.value?.state;

            if (state === FileState.Success) {
                return {};
            }

            const uploadStarted = state !== undefined && state !== FileState.Empty;

            return {
                fields: {
                    [propertyPath]: [
                        {
                            code: uploadStarted ? "QA-NOTE-003" : "QA-NOTE-002",
                            type: ValidationErrorType.Medium,
                            message: uploadStarted
                                ? "Wait for this file to finish uploading."
                                : "A file must be attached.",
                            meta: { frontEndError: true, blocking: true }
                        }
                    ]
                }
            };
        }
    }
];

type QaProgramChecklistAttachmentUploadViewProps = FileUploadInputViewProps<
    TrialQaProgramChecklistItemNote,
    TMDValidationError,
    object,
    TemporaryFileInformation,
    LookupOption
>;

/** Keeps each attachment row's form value aligned with its transient uploader state. */
const QaProgramChecklistAttachmentUploadView: FC<
    QaProgramChecklistAttachmentUploadViewProps
> = props => {
    const { fieldInput, uploadState } = props;
    const { actions, convertValue, parseValue, snapshot } = fieldInput;
    const currentValue = convertValue(snapshot.value);

    useEffect(() => {
        if (!uploadState) {
            return;
        }

        const state =
            uploadState.state === "failed" || uploadState.state === "cancelled"
                ? FileState.Empty
                : uploadState.state === "completed"
                  ? undefined
                  : FileState.Uploading;

        if (state === undefined || currentValue?.state === state) {
            return;
        }

        actions.setValue(
            parseValue({
                ...currentValue,
                state
            }) as TemporaryFileInformation,
            {
                notify: true,
                updateDirty: true,
                updateFocused: true,
                updateTouched: true
            }
        );
    }, [actions, currentValue, parseValue, uploadState]);

    return <FileUploadInputDefaultView {...props} />;
};

const attachmentColumns = [
    {
        propertyPath: "fileInformation",
        formInputComponent: FileUploadInput,
        groupedFieldProps: { view: QaProgramChecklistAttachmentUploadView }
    } as GroupedTableFieldColumn<
        typeof FileUploadInput,
        TrialQaProgramChecklistItemNote,
        TMDValidationError,
        object,
        SourceDocument,
        LookupOption
    >
];

export interface QaProgramChecklistNoteComposerProps {
    itemId: number;
    onCancel: () => void;
    onAdd: (note: TrialQaProgramChecklistItemNote) => Promise<unknown>;
}

/** Adds an immutable note and optional completed temporary-file uploads. */
const QaProgramChecklistNoteComposer: FC<QaProgramChecklistNoteComposerProps> = ({
    itemId,
    onCancel,
    onAdd
}) => {
    const initialValue = useMemo(
        (): TrialQaProgramChecklistItemNote => ({
            trialQaProgramChecklistItemId: itemId,
            comments: "",
            enteredBy: "",
            modifiedBy: "",
            attachments: []
        }),
        [itemId]
    );
    const formManager = useFormManager<TrialQaProgramChecklistItemNote, TMDValidationError>({
        initialValue,
        labels: { comments: "Message", attachments: "Attachments" },
        validators,
        allowSubmit: useTMDFormAllowSubmit<TrialQaProgramChecklistItemNote>(),
        onSubmitNotAllowed: useTMDFormOnSubmitNotAllowed(),
        onSubmitFailed: useTMDFormOnSubmitFailed(),
        onSubmit: async snapshot => {
            const note = new TrialQaProgramChecklistItemNote({
                ...snapshot.value,
                comments: snapshot.value.comments.trim()
            });
            await onAdd(note);
            return undefined;
        }
    });

    return (
        <Form formManager={formManager}>
            <FieldPropertyPathProvider propertyPath="">
                <Stack gap="sm">
                    <LongGroupedField
                        propertyPath="comments"
                        formInputComponent={TextAreaInput}
                        autosize
                        minRows={4}
                        data-autofocus
                    />
                    <GroupedTableField
                        propertyPath="attachments"
                        label="Attachments"
                        columns={attachmentColumns}
                        createItem={async () => ({ active: true }) as SourceDocument}
                        minWidth={420}
                    />
                    <Group
                        justify="flex-end"
                        p="sm"
                    >
                        <Button
                            variant="default"
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                        <FormActionButton onClick={(_event, manager) => manager.submit()}>
                            Add note
                        </FormActionButton>
                    </Group>
                </Stack>
            </FieldPropertyPathProvider>
        </Form>
    );
};

export default QaProgramChecklistNoteComposer;
